import React, { useMemo, useState } from 'react';
import { Upload, Plus, Trash2, ImagePlus, Info } from 'lucide-react';
import { uploadImageToCloudinary } from '../../utils/uploadCloudinary';
import {
  PRICING_FIXED,
  PRICING_FREE,
  VALIDITY_PRESETS,
} from '../../utils/giftCardTemplateForm';

function FormSection({ title, children, testId }) {
  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 space-y-3"
      data-testid={testId}
    >
      <h4 className="text-sm font-semibold text-gray-900 tracking-wide uppercase">
        {title}
      </h4>
      {children}
    </section>
  );
}

/**
 * Lot 4C — Formulaire template carte cadeau (Admin).
 * Active ≠ publiée catalogue ≠ achetable.
 */
export default function GiftCardTemplateForm({
  mode = 'create',
  form,
  errors = {},
  submitting = false,
  formError = null,
  onChange,
  onSubmit,
  onCancel,
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const setField = (key, value) => {
    if (submitting) return;
    onChange?.({ ...form, [key]: value });
  };

  const amountList = Array.isArray(form.amountEurosList) ? form.amountEurosList : [];

  const validityHint = useMemo(
    () => 'Cette durée sera indiquée à l’acheteur au moment de l’achat.',
    [],
  );

  const handleUpload = async (file) => {
    if (!file || submitting) return;
    setUploadError(null);
    setUploading(true);
    try {
      const url = await uploadImageToCloudinary(file, 'gift-cards');
      setField('imageUrl', url);
    } catch (err) {
      setUploadError(err?.message || 'Échec upload image');
    } finally {
      setUploading(false);
    }
  };

  const onDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file?.type?.startsWith('image/')) await handleUpload(file);
  };

  const title = mode === 'edit' ? 'Modifier la carte cadeau' : 'Créer une carte cadeau';

  return (
    <form
      className="space-y-4 min-w-0"
      data-testid="gift-card-template-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!submitting) onSubmit?.();
      }}
    >
      <div className="flex items-start justify-between gap-3 bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600 mt-1">
            Configurez l’offre. Active, publiée sur le catalogue et achetable sont trois états
            distincts.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-gray-500 hover:text-gray-800 shrink-0"
          disabled={submitting}
        >
          Fermer
        </button>
      </div>

      {formError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {formError}
        </div>
      )}

      <FormSection title="Informations" testId="gift-card-section-info">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
          <input
            type="text"
            value={form.nom || ''}
            onChange={(e) => setField('nom', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
            data-testid="gift-card-nom"
            disabled={submitting}
          />
          {errors.nom && <p className="text-xs text-red-600 mt-1">{errors.nom}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={form.description || ''}
            onChange={(e) => setField('description', e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
            data-testid="gift-card-description"
            disabled={submitting}
          />
        </div>
        <div className="flex items-start gap-2 text-xs text-gray-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          <span className="font-medium text-gray-800 shrink-0">TVA : gérée automatiquement</span>
          <span
            className="inline-flex items-center text-slate-500 cursor-help"
            title="Cette carte cadeau est traitée comme un bon multi-usage. La TVA est appliquée lors de son utilisation selon les prestations ou produits achetés."
            data-testid="gift-card-vat-help"
          >
            <Info className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">
              Cette carte cadeau est traitée comme un bon multi-usage. La TVA est appliquée lors de
              son utilisation selon les prestations ou produits achetés.
            </span>
          </span>
        </div>
      </FormSection>

      <FormSection title="Image" testId="gift-card-section-image">
        <p className="text-xs text-gray-500 -mt-1">
          Image d’illustration de la carte cadeau (futur catalogue).
        </p>
        <div
          className={`relative border-2 border-dashed rounded-lg p-4 sm:p-5 transition ${
            dragActive ? 'border-[#4A148C] bg-purple-50/50' : 'border-gray-300 bg-gray-50/50'
          }`}
          onDragEnter={onDrag}
          onDragLeave={onDrag}
          onDragOver={onDrag}
          onDrop={onDrop}
        >
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
            {form.imageUrl ? (
              <img
                src={form.imageUrl}
                alt="Aperçu carte cadeau"
                className="h-28 w-full sm:w-28 rounded-lg object-cover border border-gray-200 shrink-0"
              />
            ) : (
              <div className="h-28 w-full sm:w-28 rounded-lg border border-dashed border-gray-300 bg-white flex flex-col items-center justify-center text-gray-400 gap-1 shrink-0">
                <ImagePlus className="h-7 w-7" />
                <span className="text-xs">Aperçu</span>
              </div>
            )}
            <div className="flex-1 min-w-0 space-y-2">
              <p className="text-sm text-gray-700">
                Glissez une image ici, ou choisissez un fichier.
              </p>
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm cursor-pointer hover:bg-gray-50">
                  <Upload className="h-4 w-4" />
                  {uploading ? 'Upload…' : 'Choisir une image'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={submitting || uploading}
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                  />
                </label>
                {form.imageUrl && (
                  <button
                    type="button"
                    className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                    onClick={() => setField('imageUrl', '')}
                    disabled={submitting}
                  >
                    Retirer
                  </button>
                )}
              </div>
              {uploadError && <p className="text-xs text-red-600">{uploadError}</p>}
            </div>
          </div>
        </div>
      </FormSection>

      <FormSection title="Montant" testId="gift-card-section-montant">
        <p className="text-sm font-medium text-gray-700 mb-1">Mode de montant *</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup">
          <button
            type="button"
            role="radio"
            aria-checked={form.pricingMode === PRICING_FREE}
            data-testid="gift-card-pricing-free"
            disabled={submitting}
            onClick={() => setField('pricingMode', PRICING_FREE)}
            className={`rounded-xl border-2 px-3 py-3 text-left ${
              form.pricingMode === PRICING_FREE
                ? 'border-[#4A148C] bg-purple-50/70'
                : 'border-gray-200'
            }`}
          >
            <p className="text-sm font-semibold">Montant libre</p>
            <p className="text-xs text-gray-600 mt-1">Minimum et maximum obligatoires</p>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={form.pricingMode === PRICING_FIXED}
            data-testid="gift-card-pricing-fixed"
            disabled={submitting}
            onClick={() => setField('pricingMode', PRICING_FIXED)}
            className={`rounded-xl border-2 px-3 py-3 text-left ${
              form.pricingMode === PRICING_FIXED
                ? 'border-[#4A148C] bg-purple-50/70'
                : 'border-gray-200'
            }`}
          >
            <p className="text-sm font-semibold">Montants fixes</p>
            <p className="text-xs text-gray-600 mt-1">Liste de montants en euros</p>
          </button>
        </div>

        {form.pricingMode === PRICING_FIXED ? (
          <div className="space-y-2 pt-1" data-testid="gift-card-fixed-amounts">
            <p className="text-sm font-medium text-gray-700">Montants (€) *</p>
            {amountList.map((val, idx) => (
              <div key={idx} className="flex gap-2 items-center min-w-0">
                <input
                  type="text"
                  inputMode="decimal"
                  value={val}
                  onChange={(e) => {
                    const next = [...amountList];
                    next[idx] = e.target.value;
                    setField('amountEurosList', next);
                  }}
                  className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-2"
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="p-2 text-red-600 disabled:opacity-40 shrink-0"
                  disabled={submitting || amountList.length <= 1}
                  onClick={() =>
                    setField(
                      'amountEurosList',
                      amountList.filter((_, i) => i !== idx),
                    )
                  }
                  aria-label="Retirer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm text-[#4A148C]"
              disabled={submitting}
              onClick={() => setField('amountEurosList', [...amountList, ''])}
            >
              <Plus className="h-4 w-4" /> Ajouter un montant
            </button>
            {errors.amounts && <p className="text-xs text-red-600">{errors.amounts}</p>}
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1"
            data-testid="gift-card-free-amount"
          >
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum (€) *
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={form.minAmountEuros || ''}
                onChange={(e) => setField('minAmountEuros', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                disabled={submitting}
              />
              {errors.minAmountEuros && (
                <p className="text-xs text-red-600 mt-1">{errors.minAmountEuros}</p>
              )}
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum (€) *
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={form.maxAmountEuros || ''}
                onChange={(e) => setField('maxAmountEuros', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                disabled={submitting}
              />
              {errors.maxAmountEuros && (
                <p className="text-xs text-red-600 mt-1">{errors.maxAmountEuros}</p>
              )}
            </div>
          </div>
        )}
      </FormSection>

      <FormSection title="Validité" testId="gift-card-section-validity">
        <div className="flex flex-wrap gap-2">
          {Object.values(VALIDITY_PRESETS).map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={submitting}
              onClick={() => setField('validityPreset', p.id)}
              className={`px-3 py-1.5 rounded-full text-sm border ${
                form.validityPreset === p.id
                  ? 'bg-[#4A148C] text-white border-[#4A148C]'
                  : 'border-gray-300 text-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {form.validityPreset === 'CUSTOM' && (
          <input
            type="number"
            min={1}
            max={3650}
            value={form.customValidityDays || ''}
            onChange={(e) => setField('customValidityDays', e.target.value)}
            placeholder="Jours"
            className="mt-1 w-full sm:w-40 rounded-lg border border-gray-300 px-3 py-2"
            disabled={submitting}
          />
        )}
        {errors.customValidityDays && (
          <p className="text-xs text-red-600">{errors.customValidityDays}</p>
        )}
        <p className="text-xs text-gray-500">{validityHint}</p>
      </FormSection>

      <FormSection title="Statut & publication" testId="gift-card-section-status">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <label className="inline-flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={Boolean(form.actif)}
                onChange={(e) => setField('actif', e.target.checked)}
                disabled={submitting}
              />
              Active (prête côté Admin)
            </label>
            <span
              className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full w-fit ${
                form.catalogVisible
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-slate-100 text-slate-700'
              }`}
              data-testid="gift-card-catalog-badge"
            >
              {form.catalogVisible ? 'Publiée' : 'Non publiée'}
            </span>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={Boolean(form.catalogVisible)}
              onChange={(e) => setField('catalogVisible', e.target.checked)}
              disabled={submitting}
              data-testid="gift-card-catalog-visible"
            />
            Publiée sur le catalogue
          </label>
        </div>
        <p className="text-xs text-gray-500">
          Active ≠ publiée ≠ achetable. « Publiée sur le catalogue » = visible vitrine uniquement ;
          l’achat reste désactivé tant que l’activation purchase n’est pas livrée.
        </p>
      </FormSection>

      <div className="sticky bottom-0 z-10 -mx-0 bg-gray-50/95 backdrop-blur border-t border-gray-200 px-0 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <button
          type="button"
          className="btn-secondary w-full sm:w-auto"
          onClick={onCancel}
          disabled={submitting}
        >
          Annuler
        </button>
        <button
          type="submit"
          className="btn-primary w-full sm:w-auto"
          disabled={submitting || uploading}
          data-testid="gift-card-submit"
        >
          {submitting ? 'Enregistrement…' : mode === 'edit' ? 'Enregistrer' : 'Créer'}
        </button>
      </div>
    </form>
  );
}
